import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';
import { invalidateEntity } from '../cache-invalidation';
import { systemKeys } from '../query-keys';
import {
  addSystemContract,
  updateSystemContract,
  deleteSystemContract,
  type AddSystemRequest,
  type UpdateSystemRequest,
  type AddSystemResponse,
  type UpdateSystemResponse,
  type DeleteSystemResponse
} from '@bliks-io/contracts';

/**
 * Create system mutation
 *
 * The invalidation is returned, so `mutateAsync` resolves only once the
 * refetch has landed and callers can render the new system by id right away.
 */
export function useCreateSystem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AddSystemRequest): Promise<AddSystemResponse> => {
      const { data: response } = await apiClient.post<AddSystemResponse>(
        addSystemContract.path,
        data
      );
      return response;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: systemKeys.lists() })
  });
}

/**
 * Update system mutation
 */
export function useUpdateSystem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data
    }: {
      id: string;
      data: UpdateSystemRequest;
    }): Promise<UpdateSystemResponse> => {
      const path = updateSystemContract.path.replace(':id', id);
      const { data: response } = await apiClient.patch<UpdateSystemResponse>(path, data);
      return response;
    },
    onSuccess: () => {
      invalidateEntity(queryClient, 'system');
    }
  });
}

/**
 * Delete system mutation
 */
export function useDeleteSystem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<DeleteSystemResponse> => {
      const path = deleteSystemContract.path.replace(':id', id);
      const { data } = await apiClient.delete<DeleteSystemResponse>(path);
      return data;
    },
    onSuccess: (_, id) => {
      invalidateEntity(queryClient, 'system');
      queryClient.removeQueries({ queryKey: systemKeys.detail(id) });
    }
  });
}
